import { TaskStatus, TaskView } from '@ndvr/customer-api';

import { MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { convertPlainTextToHtml, formatDate, titleCase } from 'utils';
import { useHouseholdMap } from 'views/household/household-queries';

import { useTaskMentionSuggestions } from '../task-queries';
import { getTaskAssigneeLabel } from '../task-utils';
import { TaskActivitySection } from './TaskActivitySection';
import { TaskStatusBadge } from './TaskBadge';
import { TaskCommentSection } from './TaskCommentSection';
import { TaskRecurrenceChip } from './TaskRecurrenceChip';
import { TaskTypeIcon } from './TaskTypeIcon';

export interface TaskViewDetailsContentProps {
    task: TaskView;
    currentPersonOrgGUID: string | undefined;
    commentDraft: string;
    onCommentDraftChange: (draft: string) => void;
    onHouseholdClick?: () => void;
}

export const TaskViewDetailsContent = ({
    task,
    currentPersonOrgGUID,
    commentDraft,
    onCommentDraftChange,
    onHouseholdClick,
}: TaskViewDetailsContentProps) => {
    const householdMap = useHouseholdMap();
    const mentionSuggestions = useTaskMentionSuggestions(task.householdGUID ?? undefined);

    const householdName = task.householdGUID
        ? householdMap.get(task.householdGUID)?.globalAccountNamePlain || 'Unknown Household'
        : 'Not assigned';

    const assigneeName = getTaskAssigneeLabel(task);

    return (
        <div className="space-y-4">
            <Accordion type="single" collapsible defaultValue="description" className="rounded-md border">
                <AccordionItem value="description" className="border-0">
                    <AccordionTrigger className="px-3 py-2 text-sm font-medium">Description</AccordionTrigger>
                    <AccordionContent className="px-3">
                        {task.description ? (
                            <MinimalTiptapEditorNoToolbar
                                key={task.description} // re-mount editor when description changes
                                value={convertPlainTextToHtml(task.description)}
                                className="w-full border-0 text-sm shadow-none"
                                output="html"
                                editable={false}
                                editorClassName="focus:outline-hidden"
                            />
                        ) : (
                            <p className="text-sm">No description provided</p>
                        )}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            <div className="space-y-3 rounded-md border p-4">
                <div className="grid grid-cols-3 items-center gap-3">
                    <Label className="text-sm font-medium">Household:</Label>
                    {task.householdGUID && onHouseholdClick ? (
                        <button
                            type="button"
                            className="col-span-2 truncate text-left text-sm text-blue-600 hover:underline"
                            onClick={onHouseholdClick}
                        >
                            {householdName}
                        </button>
                    ) : (
                        <span className="col-span-2 truncate text-sm">{householdName}</span>
                    )}
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                    <Label className="text-sm font-medium">Assigned To:</Label>
                    <span className="col-span-2 text-sm">{assigneeName}</span>
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                    <Label className="text-sm font-medium">Due:</Label>
                    {task.overdue && task.status !== TaskStatus.COMPLETED ? (
                        <span className="col-span-2 text-sm font-medium text-red-600">
                            {formatDate(task.dueDate)} (Overdue)
                        </span>
                    ) : (
                        <span className="col-span-2 text-sm">{formatDate(task.dueDate)}</span>
                    )}
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                    <Label className="text-sm font-medium">Type:</Label>
                    <div className="col-span-2 flex items-center space-x-1">
                        <TaskTypeIcon type={task.type} />
                        <span className="text-sm">{titleCase(task.type)}</span>
                    </div>
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                    <Label className="text-sm font-medium">Status:</Label>
                    <div className="col-span-2">
                        <TaskStatusBadge status={task.status} />
                    </div>
                </div>

                {task.frequency && (
                    <div className="grid grid-cols-3 items-center gap-3">
                        <Label className="text-sm font-medium">Frequency:</Label>
                        <div className="col-span-2 flex items-center gap-2 text-sm">
                            <span>{titleCase(task.frequency)}</span>
                            <TaskRecurrenceChip frequency={task.frequency} />
                        </div>
                    </div>
                )}
            </div>

            <div className="rounded-md border p-4">
                <TaskCommentSection
                    taskGuid={task.taskGUID}
                    draft={commentDraft}
                    onDraftChange={onCommentDraftChange}
                    mentionSuggestions={mentionSuggestions}
                />
            </div>

            <div className="rounded-md border p-4">
                <TaskActivitySection
                    taskGuid={task.taskGUID}
                    tenantGUID={task.tenantGUID}
                    householdGUID={task.householdGUID}
                    currentPersonOrgGUID={currentPersonOrgGUID}
                    mentionSuggestions={mentionSuggestions}
                />
            </div>
        </div>
    );
};
